/* ==========================================================================
   FILE: modules/brain/components/SettingsPanel.jsx
   IAMONEAI - Brain Settings Panel
   Main settings interface for user preferences and configuration
   ========================================================================== */

import React, { useState, useCallback } from 'react';
import { Button } from '../../../global/components';
import { Toggle, ToggleCard } from '../../../global/components';
import { useAPI } from '../../../global/hooks/useAPI';
import PersonalitySettings from './PersonalitySettings';
import GuardrailSettings from './GuardrailSettings';
import ContextSettings from './ContextSettings';
import './SettingsPanel.css';

// ==========================================================================
// SETTINGS SECTIONS
// ==========================================================================

const SECTIONS = {
  PERSONALITY: 'personality',
  GUARDRAILS: 'guardrails',
  CONTEXTS: 'contexts',
  PREFERENCES: 'preferences',
  PRIVACY: 'privacy',
};

// ==========================================================================
// SETTINGS PANEL COMPONENT
// ==========================================================================

/**
 * Main settings panel for brain configuration
 * 
 * @param {Object} props
 * @param {string} iin - User's IIN
 * @param {Object} brain - Current brain data
 * @param {Function} onUpdate - Called when settings are updated
 * @param {Function} onClose - Close settings panel
 */
function SettingsPanel({
  iin,
  brain = {},
  onUpdate,
  onClose,
  className = '',
}) {
  // State
  const [activeSection, setActiveSection] = useState(SECTIONS.PERSONALITY);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [localBrain, setLocalBrain] = useState(brain);
  const [message, setMessage] = useState({ type: '', text: '' });

  // API
  const api = useAPI();

  // ==========================================================================
  // UPDATE HANDLERS
  // ==========================================================================

  const handleLocalUpdate = useCallback((path, value) => {
    setLocalBrain(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return updated;
    });
    setHasChanges(true);
    setMessage({ type: '', text: '' });
  }, []);

  const handleSave = useCallback(async () => {
    if (!hasChanges || !iin) return;

    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await api.updateBrain(iin, localBrain);
      onUpdate?.(localBrain);
      setHasChanges(false);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (err) {
      console.error('Save settings error:', err);
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  }, [hasChanges, iin, localBrain, api, onUpdate]);

  const handleReset = useCallback(() => {
    setLocalBrain(brain);
    setHasChanges(false);
    setMessage({ type: '', text: '' });
  }, [brain]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  const panelClasses = [
    'settings-panel',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={panelClasses}>
      {/* Header */}
      <div className="settings-panel-header">
        <div className="settings-panel-title-row">
          <h2 className="settings-panel-title">Settings</h2>
          {onClose && (
            <button
              className="settings-panel-close"
              onClick={onClose}
              aria-label="Close settings"
            >
              <CloseIcon />
            </button>
          )}
        </div>
        <p className="settings-panel-subtitle">
          Customize how your AI guardian works for you
        </p>
      </div>

      {/* Navigation */}
      <nav className="settings-panel-nav">
        {Object.entries(SECTION_INFO).map(([key, info]) => (
          <button
            key={key}
            className={`settings-nav-item ${activeSection === key ? 'settings-nav-item-active' : ''}`}
            onClick={() => setActiveSection(key)}
          >
            <span className="settings-nav-icon">{info.icon}</span>
            <span className="settings-nav-label">{info.label}</span>
          </button>
        ))}
      </nav>

      {/* Content */}
      <div className="settings-panel-content">
        {/* Message */}
        {message.text && (
          <div className={`settings-message settings-message-${message.type}`}>
            {message.type === 'success' ? <CheckIcon /> : <AlertIcon />}
            <span>{message.text}</span>
            <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
          </div>
        )}

        {/* Sections */}
        {activeSection === SECTIONS.PERSONALITY && (
          <PersonalitySettings
            personality={localBrain.personality || {}}
            communicationStyle={localBrain.communication_style || {}}
            onUpdate={handleLocalUpdate}
          />
        )}

        {activeSection === SECTIONS.GUARDRAILS && (
          <GuardrailSettings
            guardrails={localBrain.guardrails || {}}
            onUpdate={handleLocalUpdate}
          />
        )}

        {activeSection === SECTIONS.CONTEXTS && (
          <ContextSettings
            contexts={localBrain.contexts || {}}
            activeContext={localBrain.activeContext || 'personal'}
            onUpdate={handleLocalUpdate}
          />
        )}

        {activeSection === SECTIONS.PREFERENCES && (
          <PreferencesSection
            brain={localBrain}
            onUpdate={handleLocalUpdate}
          />
        )}

        {activeSection === SECTIONS.PRIVACY && (
          <PrivacySection
            brain={localBrain}
            onUpdate={handleLocalUpdate}
          />
        )}
      </div>

      {/* Footer */}
      <div className="settings-panel-footer">
        {hasChanges && (
          <span className="settings-unsaved">You have unsaved changes</span>
        )}
        <div className="settings-panel-actions">
          <Button
            variant="ghost"
            onClick={handleReset}
            disabled={!hasChanges || isSaving}
          >
            Reset
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={isSaving}
            disabled={!hasChanges}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// SECTION INFO
// ==========================================================================

const SECTION_INFO = {
  [SECTIONS.PERSONALITY]: {
    label: 'Personality',
    icon: '🎭',
  },
  [SECTIONS.GUARDRAILS]: {
    label: 'Guardrails',
    icon: '🛡️',
  },
  [SECTIONS.CONTEXTS]: {
    label: 'Contexts',
    icon: '🔄',
  },
  [SECTIONS.PREFERENCES]: {
    label: 'Preferences',
    icon: '⚙️',
  },
  [SECTIONS.PRIVACY]: {
    label: 'Privacy',
    icon: '🔒',
  },
};

// ==========================================================================
// PREFERENCES SECTION
// ==========================================================================

function PreferencesSection({ brain, onUpdate }) {
  const autonomic = brain.guardrails?.autonomic || {};

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h3 className="settings-section-title">Preferences</h3>
        <p className="settings-section-desc">
          Control automatic behaviors and features
        </p>
      </div>

      <div className="settings-section-content">
        <ToggleCard
          title="Auto-Memory"
          description="Automatically save important things you share"
          icon="💾"
          checked={autonomic.autoMemoryEnabled !== false}
          onChange={(checked) => onUpdate('guardrails.autonomic.autoMemoryEnabled', checked)}
        />

        <ToggleCard
          title="Emotional Awareness"
          description="Detect and respond to your emotional state"
          icon="💭"
          checked={autonomic.emotionalAwarenessEnabled !== false}
          onChange={(checked) => onUpdate('guardrails.autonomic.emotionalAwarenessEnabled', checked)}
        />

        <ToggleCard
          title="Health Check-ins"
          description="Follow up when you mention health concerns"
          icon="🏥"
          checked={autonomic.healthFollowUpEnabled !== false}
          onChange={(checked) => onUpdate('guardrails.autonomic.healthFollowUpEnabled', checked)}
        />

        <ToggleCard
          title="Goal Tracking"
          description="Automatically detect and track your goals"
          icon="🎯"
          checked={autonomic.goalTrackingEnabled !== false}
          onChange={(checked) => onUpdate('guardrails.autonomic.goalTrackingEnabled', checked)}
        />
      </div>
    </div>
  );
}

// ==========================================================================
// PRIVACY SECTION
// ==========================================================================

function PrivacySection({ brain, onUpdate }) {
  const guardrails = brain.guardrails || {};

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h3 className="settings-section-title">Privacy & Security</h3>
        <p className="settings-section-desc">
          Control how your data is handled
        </p>
      </div>

      <div className="settings-section-content">
        <ToggleCard
          title="Memory Retention"
          description={`Keep memories for ${guardrails.autonomic?.memoryRetentionDays || 90} days`}
          icon="📅"
          checked={true}
          onChange={() => {}}
          disabled
        />

        <ToggleCard
          title="Block Illegal Content"
          description="Refuse to assist with illegal activities"
          icon="🚫"
          checked={guardrails.blockIllegal !== false}
          onChange={(checked) => onUpdate('guardrails.blockIllegal', checked)}
          disabled // Always on
        />

        <ToggleCard
          title="Block Self-Harm Content"
          description="Refuse to assist with self-harm"
          icon="❤️"
          checked={guardrails.blockSelfHarm !== false}
          onChange={(checked) => onUpdate('guardrails.blockSelfHarm', checked)}
          disabled // Always on
        />

        <div className="settings-privacy-note">
          <LockIcon />
          <span>
            Your data is stored securely and never shared with third parties.
            You own your data completely.
          </span>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// ICONS
// ==========================================================================

function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default SettingsPanel;